import React from "react";
import { GlossaryBlockForm } from "../../ObjectBlockForm";
// Basic Info Component removed - now in structure-menu sidebar
// import { GlossaryBasicInfoComponent } from "../../ObjectBasicInfoComponent";
import CodePreview from "../../../blueprints/blueprint_wizard/CodePreview";
import { BlueprintVersionWarning } from "./BlueprintVersionWarning";
import { ViewModeSelector } from "./ViewModeSelector";
import { MigrationComparisonView } from "@/components/migration/MigrationComparisonView";
import { MigrationPreviewData } from "@/hooks/useMigrationPreview";
import { LoadingSpinner } from "@/components/loaders/loading-spinner";
import { IconProvider } from "@/components/ui";
import { GlossaryBlueprint } from "@/types-games";
import { sectionDataObject } from "@/lib/stores/objectsStore";
import { BlueprintVersionChanges } from "@/lib/utils/blueprintVersionUtils";
import { usePlatformTranslation } from "@/hooks/usePlatformTranslation";
import { WizardMode, ViewDisplayMode } from "../types";

interface ObjectWizardContentProps {
  // Display state
  inline: boolean;
  wizardMode: WizardMode;
  viewDisplayMode: ViewDisplayMode;
  availableViewModes: string[];
  
  // Data
  initialData: any;
  glossaryBlueprints: GlossaryBlueprint[];
  isLoading: boolean;
  gameId: any;
  codePreview: string | null;
  blueprintChanges: BlueprintVersionChanges | null;
  migrationPreviewData: MigrationPreviewData | null;
  migrationPreviewLoading: boolean;
  migrationPreviewError: string | null;
  
  // State from glossaryState
  glossaryState: {
    selectedBlueprint: GlossaryBlueprint | null;
    selectedBlueprintId: string;
    parsedSections: sectionDataObject[];
    itemTags: string[];
    itemIdentifiers: string[];
    itemDescription: string;
    owner: string | null;
    editPermissions: 'Open' | 'Locked';
    setItemTags: (tags: string[]) => void;
    setItemIdentifiers: (ids: string[]) => void;
    setItemDescription: (desc: string) => void;
    setOwner: (owner: string | null) => void;
    setEditPermissions: (perms: 'Open' | 'Locked') => void;
  };
  
  // Permissions
  canEdit: boolean;
  
  // Handlers
  onBlueprintChange: (blueprintId: string) => void;
  onSectionsChange: (sections: sectionDataObject[]) => void;
  onFormCancel: () => void;
  onViewModeChange: (mode: ViewDisplayMode) => void;
  onViewMigration: () => void;
  onApplyMigration: (fieldUpdates: Map<string, Map<string, any>>) => Promise<void>;
}

/**
 * Main content renderer for the Object Wizard
 * Handles switching between different view modes and rendering the appropriate content
 */
export const ObjectWizardContent: React.FC<ObjectWizardContentProps> = ({
  inline,
  wizardMode,
  viewDisplayMode,
  availableViewModes,
  initialData,
  glossaryBlueprints,
  isLoading,
  gameId,
  codePreview,
  blueprintChanges,
  migrationPreviewData,
  migrationPreviewLoading,
  migrationPreviewError,
  glossaryState,
  canEdit,
  onBlueprintChange,
  onSectionsChange,
  onFormCancel,
  onViewModeChange,
  onViewMigration,
  onApplyMigration,
}) => {
  const { t } = usePlatformTranslation();
  
  // Handler for saving migration changes
  const handleMigrationSave = async (fieldUpdates: Map<string, Map<string, any>>) => {
    // Apply the migration (updates sections, saves object, and updates blueprint version)
    await onApplyMigration(fieldUpdates);
    // Switch back to structured view after saving
    onViewModeChange("structured");
  };

  // Only show form when we have both blueprint and sections
  const shouldShowForm =
    glossaryState.selectedBlueprint &&
    glossaryState.parsedSections &&
    glossaryState.parsedSections.length > 0;

  // Handle migration view
  if (viewDisplayMode === "migration") {
    if (migrationPreviewLoading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" text="Loading migration preview..." />
        </div>
      );
    }

    if (migrationPreviewError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <IconProvider iconstring="fa-exclamation-triangle" size={48} className="text-destructive" />
            <h2 className="text-xl font-semibold text-destructive">
              Failed to Load Migration Preview
            </h2>
            <p className="text-muted-foreground">{migrationPreviewError}</p>
            <button
              onClick={() => onViewModeChange("structured")}
              className="px-4 py-2 bg-muted hover:bg-muted/80 rounded transition-colors text-foreground"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    if (!migrationPreviewData) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <IconProvider iconstring="fa-question-circle" size={48} className="text-muted-foreground" />
            <h2 className="text-xl font-semibold text-muted-foreground">
              No Preview Data Available
            </h2>
            <button
              onClick={() => onViewModeChange("structured")}
              className="px-4 py-2 bg-muted hover:bg-muted/80 rounded transition-colors text-foreground"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={inline ? "max-w-7xl mx-auto px-6 py-6" : "px-6 py-6"}>
        <div className="mb-4">
          <button
            onClick={() => onViewModeChange("structured")}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <IconProvider iconstring="fa-arrow-left" size={14} />
            Back to Object
          </button>
        </div>
        <MigrationComparisonView
          currentStructure={migrationPreviewData.object.data}
          changes={migrationPreviewData.preview.changes}
          blueprintName={migrationPreviewData.blueprint.name}
          currentVersion={migrationPreviewData.preview.currentVersion}
          targetVersion={migrationPreviewData.preview.targetVersion}
          onSaveChanges={handleMigrationSave}
        />
      </div>
    );
  }

  return (
    <>
      {/* View Mode Selector - Only show for code preview modes (Unity, Godot, etc.), not for regular edit mode */}
      {(viewDisplayMode !== "structured" && viewDisplayMode !== "documentation" && viewDisplayMode !== "migration") && (
        <ViewModeSelector
          viewDisplayMode={viewDisplayMode}
          onViewModeChange={onViewModeChange}
          availableViewModes={availableViewModes}
          inline={inline}
        />
      )}

      <div className={inline ? "max-w-4xl mx-auto px-6 py-6" : ""}>
        {/* Blueprint Version Mismatch Warning */}
        <BlueprintVersionWarning
          blueprintChanges={blueprintChanges!}
          wizardMode={wizardMode}
          itemId={initialData?.id}
          onViewMigration={onViewMigration}
        />

        {codePreview ? (
          // Code Preview Mode (Unity/Godot/C++/TypeScript/Roblox/JSON)
          <CodePreview
            code={codePreview}
            language={
              viewDisplayMode === "unity"
                ? "csharp"
                : viewDisplayMode === "godot"
                ? "gdscript"
                : viewDisplayMode === "cpp"
                ? "cpp"
                : viewDisplayMode === "typescript"
                ? "typescript"
                : viewDisplayMode === "roblox"
                ? "lua"
                : viewDisplayMode === "json"
                ? "json"
                : "csharp"
            }
            gameName={gameId?.name}
            templateName={initialData?.name}
          />
        ) : (
          <>
            {/* Basic Information moved to structure-menu sidebar (StructureSidebar-BasicInfo.tsx) */}
            {/* Can be edited independently without entering edit mode */}

            {/* Show blueprint selector when creating new item */}
            {wizardMode === "create" && !glossaryState.selectedBlueprint && (
              <div className="mb-4 p-4 border border-border rounded bg-muted">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select Template
                </label>
                <select
                  value={glossaryState.selectedBlueprintId}
                  onChange={(e) => onBlueprintChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  disabled={isLoading}
                >
                  <option value="" disabled>
                    Select Template to create new Object
                  </option>
                  {glossaryBlueprints.map((blueprint) => (
                    <option key={blueprint.id} value={blueprint.id}>
                      {blueprint.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Form Content - Show when we have required data */}
            {shouldShowForm && (
              <div className="mt-0">
                <GlossaryBlockForm
                  blueprint={glossaryState.selectedBlueprint}
                  onCancel={onFormCancel}
                  initialData={initialData}
                  sections={glossaryState.parsedSections}
                  onSectionsChange={onSectionsChange}
                  mode={wizardMode}
                  canEdit={canEdit}
                  blueprintChanges={blueprintChanges}
                  showDocumentation={viewDisplayMode === "documentation"}
                />
              </div>
            )}
          </>
        )}

        {/* Loading State - Show when waiting for data */}
        {glossaryState.selectedBlueprint && !shouldShowForm && (
          <div className="mt-6 flex items-center justify-center p-8">
            <div className="text-muted-foreground">
              {t("platform.wizards.preparing_form")}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

